import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';

const ADSERVER_URL = process.env.REACT_APP_ADSERVER_URL || '';

const AdvertisePage: React.FC = () => {
  const [searchQ, setSearchQ] = useState('');
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQ.trim()) navigate(`/search?q=${encodeURIComponent(searchQ)}`);
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', color: '#fff', padding: isMobile ? '40px 16px' : '80px 20px', textAlign: 'center' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <h1 style={{ margin: '0 0 16px', fontSize: '40px', fontWeight: 900, lineHeight: 1.2 }}>
            Grow Your Treatment Center
          </h1>
          <p style={{ margin: '0 0 40px', fontSize: '18px', opacity: 0.9, lineHeight: 1.6 }}>
            Reach individuals and families actively searching for addiction treatment. TopRatedRehabs connects you with people who need help — right now.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Search for your facility by name..."
                style={{ padding: '14px 20px', borderRadius: '8px', border: 'none', fontSize: '16px', width: isMobile ? '100%' : '320px', maxWidth: '100%' }}
              />
              <button type="submit" style={{ padding: '14px 28px', background: '#f5a623', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '16px', cursor: 'pointer' }}>
                Find &amp; Claim
              </button>
            </form>
          </div>
          <p style={{ margin: '16px 0 0', opacity: 0.7, fontSize: '14px' }}>
            Not listed yet?{' '}
            <Link to="/add-listing" style={{ color: '#f5a623', fontWeight: 700 }}>Add your facility →</Link>
          </p>
        </div>
      </div>

      {/* Benefits */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '60px 20px' }}>
        <h2 style={{ textAlign: 'center', fontSize: '30px', fontWeight: 800, color: '#1a237e', marginBottom: '40px' }}>
          Why Advertise on TopRatedRehabs?
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: '24px' }}>
          {[
            { icon: '🎯', title: 'High-Intent Traffic', desc: 'Our visitors are actively seeking treatment — not casually browsing. Every visit is a potential admission.' },
            { icon: '⭐', title: 'VIP Listing Placement', desc: 'VIP listings appear at the top of search results with premium placement, photos, and badges.' },
            { icon: '📊', title: 'Analytics Dashboard', desc: 'Track views, clicks, and calls from your private owner dashboard.' },
            { icon: '✍️', title: 'Managed Profile', desc: 'Control your facility description, photos, staff bios, specializations, and more.' },
            { icon: '💬', title: 'Review Management', desc: 'Respond to reviews publicly and build trust with prospective patients and families.' },
            { icon: '🔍', title: 'SEO Visibility', desc: 'Your listing is indexed by Google with structured data, city pages, and state directory pages.' },
          ].map(b => (
            <div key={b.title} style={{ padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e8eaf6', borderTop: '4px solid #1a237e' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>{b.icon}</div>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#1a237e' }}>{b.title}</h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#555', lineHeight: 1.6 }}>{b.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div style={{ background: '#f5f5ff', padding: '60px 20px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '30px', fontWeight: 800, color: '#1a237e', marginBottom: '12px' }}>Simple Pricing</h2>
          <p style={{ color: '#555', marginBottom: '40px', fontSize: '16px' }}>No setup fees. No long-term contracts. Cancel anytime.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '20px' }}>
            {[
              { name: 'Free Listing', price: '$0/mo', features: ['Basic directory listing', 'Contact info', 'Address & map', 'Standard search placement'], cta: 'Claim Free', to: '/search', highlight: false },
              { name: 'VIP Listing', price: 'From $299/mo', features: ['Everything in Free', 'Top search placement', 'Photos & staff bios', 'Review responses', 'Analytics dashboard', 'Featured badge'], cta: 'Get Started', to: '/search', highlight: true },
            ].map(p => (
              <div key={p.name} style={{ padding: '32px', background: p.highlight ? '#1a237e' : '#fff', color: p.highlight ? '#fff' : 'inherit', borderRadius: '12px', border: p.highlight ? 'none' : '1px solid #e0e0e0' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 800 }}>{p.name}</h3>
                <div style={{ fontSize: '28px', fontWeight: 900, color: p.highlight ? '#f5a623' : '#1a237e', margin: '8px 0 20px' }}>{p.price}</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', textAlign: 'left' }}>
                  {p.features.map(f => <li key={f} style={{ padding: '6px 0', fontSize: '14px', opacity: p.highlight ? 0.9 : 0.8 }}>✓ {f}</li>)}
                </ul>
                <Link to={p.to} style={{ display: 'block', padding: '12px', background: p.highlight ? '#f5a623' : '#1a237e', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 700, textAlign: 'center' }}>
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 800, color: '#1a237e', marginBottom: '16px' }}>Ready to get started?</h2>
        <p style={{ color: '#555', marginBottom: '28px', fontSize: '16px' }}>Search for your facility to claim your free listing, or add it if it's not in our directory yet.</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/search" style={{ padding: '14px 32px', background: '#1a237e', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 700, fontSize: '16px' }}>
            Find My Facility
          </Link>
          <Link to="/add-listing" style={{ padding: '14px 32px', background: '#f5a623', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 700, fontSize: '16px' }}>
            Add New Listing
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdvertisePage;
